/**
 * Recommended actions above the composer: rules from live data plus LLM chips
 * the backend caches per user and scope. A persisted TanStack query, so a
 * revisit shows the last chips while it revalidates. Never blocks the
 * dashboard: on error the chips are simply absent.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { queryClient } from "@/lib/query/queryClient";

import { DashboardAPI } from "./api/dashboardApi";
import type {
  DashboardScope,
  Recommendation,
  RecommendationsResponse
} from "./types";

const NO_ITEMS: Recommendation[] = [];

export const recommendationsQueryKey = (
  kind: DashboardScope["kind"] | null,
  id: string | null
) => ["dashboard-recs", kind, id] as const;

export type RecommendationsState = {
  items: Recommendation[];
  isLoading: boolean;
  error: string | null;
  /** Re-run the model (`?refresh=1`). */
  regenerate: () => void;
  /** Re-read (rules follow live data; the LLM chips stay cached). */
  reload: () => Promise<void>;
};

export const useRecommendations = (
  scope: DashboardScope | null
): RecommendationsState => {
  const kind = scope?.kind ?? null;
  const id = scope?.id ?? null;
  const enabled = Boolean(kind && id);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  const query = useQuery(
    {
      queryKey: recommendationsQueryKey(kind, id),
      queryFn: () =>
        DashboardAPI.getRecommendations({
          kind: kind as DashboardScope["kind"],
          id: id as string
        }),
      enabled,
      meta: { persist: true }
    },
    queryClient
  );

  const regenerate = useCallback(async () => {
    if (!kind || !id) return;
    setRegenerating(true);
    try {
      const res = await DashboardAPI.getRecommendations(
        { kind, id },
        { refresh: true }
      );
      queryClient.setQueryData<RecommendationsResponse>(
        recommendationsQueryKey(kind, id),
        res
      );
      setRegenerateError(null);
    } catch (e) {
      setRegenerateError(
        e instanceof Error ? e.message : "Couldn't load recommendations."
      );
    } finally {
      setRegenerating(false);
    }
  }, [id, kind]);

  const { refetch } = query;
  const reload = useCallback(async () => {
    if (!enabled) return;
    await refetch();
  }, [enabled, refetch]);

  const items = Array.isArray(query.data?.items) ? query.data.items : NO_ITEMS;
  const queryError = query.error
    ? query.error instanceof Error
      ? query.error.message
      : "Couldn't load recommendations."
    : null;
  return {
    items,
    isLoading: regenerating || (enabled && !query.data && query.isPending),
    error: regenerateError ?? queryError,
    regenerate: () => void regenerate(),
    reload
  };
};
